import React, { useState, cloneElement, type ChangeEvent, type FormEvent } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, MailIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
type CampaignInterestValues = {
  contactName: string;
  workEmail: string;
  company: string;
  role: string;
  campaignReason: string;
  goals: string;
  plannedReach: string;
  budgetRange: string;
  geography: string;
  timing: string;
  productCategory: string;
  notes: string;
  consent: boolean;
};
type FormErrors = Partial<Record<keyof CampaignInterestValues, string>>;
type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';
const initialValues: CampaignInterestValues = {
  contactName: '',
  workEmail: '',
  company: '',
  role: '',
  campaignReason: '',
  goals: '',
  plannedReach: '',
  budgetRange: '',
  geography: '',
  timing: '',
  productCategory: '',
  notes: '',
  consent: false
};
const requiredFields: Array<keyof Omit<CampaignInterestValues, 'notes'>> = ['contactName', 'workEmail', 'company', 'role', 'campaignReason', 'goals', 'plannedReach', 'budgetRange', 'geography', 'timing', 'productCategory', 'consent'];
const fieldLabels: Record<keyof CampaignInterestValues, string> = {
  contactName: 'Contact name',
  workEmail: 'Work email',
  company: 'Company',
  role: 'Role',
  campaignReason: 'Campaign reason or use case',
  goals: 'Campaign goals',
  plannedReach: 'Planned reach',
  budgetRange: 'Budget range',
  geography: 'Target geography or location',
  timing: 'Target timing',
  productCategory: 'Product or category',
  notes: 'Additional notes',
  consent: 'Consent acknowledgement'
};
const inputClassName = 'mt-2 min-h-12 w-full border bg-white px-3.5 text-base text-[#11110f] outline-none transition-colors placeholder:text-black/45 focus:border-[#ff4d17] focus:ring-2 focus:ring-[#ff4d17]/35 disabled:cursor-not-allowed disabled:bg-black/5';
export function CampaignInterestForm() {
  const [values, setValues] = useState<CampaignInterestValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const reduceMotion = useReducedMotion();
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = event.target;
    const fieldName = name as keyof CampaignInterestValues;
    if (fieldName === 'consent') {
      setValues(current => ({
        ...current,
        consent: (event.target as HTMLInputElement).checked
      }));
    } else {
      setValues(current => ({
        ...current,
        [fieldName]: value
      }));
    }
    setErrors(current => ({
      ...current,
      [fieldName]: undefined
    }));
    if (status === 'error') {
      setStatus('idle');
      setStatusMessage('');
    }
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateValues(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('error');
      setStatusMessage('Please review the highlighted required fields and try again.');
      focusFirstError(nextErrors);
      return;
    }
    setStatus('submitting');
    setStatusMessage('Sending your campaign brief…');
    const formData = new FormData();
    formData.append('access_key', '68af21c6-8013-4f37-af8e-759e80bf1141');
    formData.append('subject', 'Qualified campaign interest — Sampler');
    formData.append('from_name', 'Sampler campaign-interest form');
    formData.append('replyto', values.workEmail);
    formData.append('recipient_context', 'info@samplerapp.com');
    formData.append('form_name', 'Qualified campaign interest');
    formData.append('contact_name', values.contactName);
    formData.append('work_email', values.workEmail);
    formData.append('company', values.company);
    formData.append('role', values.role);
    formData.append('campaign_reason_or_use_case', values.campaignReason);
    formData.append('campaign_goals', values.goals);
    formData.append('planned_reach', values.plannedReach);
    formData.append('budget_range', values.budgetRange);
    formData.append('target_geography_or_location', values.geography);
    formData.append('target_timing', values.timing);
    formData.append('product_or_category', values.productCategory);
    formData.append('additional_notes', values.notes);
    formData.append('privacy_consent_acknowledged', 'Yes');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const result = (await response.json()) as {
        success?: boolean;
        message?: string;
      };
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'The form could not be sent.');
      }
      setValues(initialValues);
      setStatus('success');
      setStatusMessage('Thanks — your campaign brief is on its way to the Sampler team.');
    } catch {
      setStatus('error');
      setStatusMessage('We could not send your brief right now. Please try again or email info@samplerapp.com directly.');
    }
  };
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setStatus('idle');
    setStatusMessage('');
  };
  return <div className="mx-auto mt-14 max-w-[1120px] text-left" data-id="element-233" data-mp="2o6i914p1jk9c">
      <div className="border border-black/35 bg-[#f7f5ef] p-5 shadow-[8px_8px_0_#11110f] sm:p-8 md:p-10" data-id="element-234" data-mp="2s8loe4etz8zw">
        <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr]" data-id="element-235" data-mp="2x7gi41ec6hys">
          <div data-id="element-236" data-mp="2x65crx58t25k">
            <p className="text-xs font-extrabold tracking-[0.15em] text-black/60" data-id="element-237" data-mp="3271img95mydm">
              CAMPAIGN INTEREST
            </p>
            <h3 className="mt-5 font-display text-4xl font-bold leading-[0.9] md:text-5xl" data-id="element-238" data-mp="12uz0b9c9oa1j">
              Give us the shape of the drop.
            </h3>
            <p className="mt-6 max-w-sm text-base leading-relaxed text-black/75" data-id="element-239" data-mp="17uv2d31elreg">
              Share enough context for a useful first conversation. Fields
              marked
              <span className="font-extrabold" data-id="element-240" data-mp="vsk0mutlvx21"> * </span>
              are required.
            </p>
            <div className="mt-10 border-t border-black/15 pt-5" data-id="element-241" data-mp="19rtis01dh18n">
              <p className="text-xs font-extrabold tracking-[0.12em] text-black/60" data-id="element-242" data-mp="14qdulz3weem1">
                PREFER EMAIL?
              </p>
              <a href="mailto:info@samplerapp.com?subject=Campaign%20interest" className="mt-3 inline-flex items-center gap-2 text-sm font-extrabold underline decoration-[#ff4d17] decoration-2 underline-offset-4 hover:text-black/65" data-id="element-243" data-mp="1ipdhp2brilg3">
                <MailIcon size={17} aria-hidden="true" data-id="element-244" data-mp="1ni6qc1acqdxg" />
                info@samplerapp.com
              </a>
              
            </div>
          </div>

          <div data-id="element-245" data-mp="22ffigx7hblq8">
            <AnimatePresence mode="wait" initial={false} data-id="element-246" data-mp="22ctch208lea4">
              {status === 'success' ? <motion.div key="success" initial={reduceMotion ? false : {
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduceMotion ? undefined : {
              opacity: 0,
              y: -8
            }} transition={{
              duration: reduceMotion ? 0 : 0.28,
              ease: [0.22, 1, 0.36, 1]
            }} className="flex min-h-full flex-col border-2 border-[#11110f] bg-white p-6 sm:p-8" role="status" aria-live="polite" data-id="element-247" data-mp="1xhxk20r38owh">
                  <CheckCircle2Icon size={34} className="text-[#ff4d17]" aria-hidden="true" data-id="element-248" data-mp="224djues52yiq" />
                  <p className="mt-8 text-xs font-extrabold tracking-[0.14em] text-[#ff4d17]" data-id="element-249" data-mp="1odh2xl7ks5ri">
                    BRIEF RECEIVED
                  </p>
                  <h4 className="mt-4 font-display text-4xl font-bold leading-[0.9]" data-id="element-250" data-mp="1tctnfzaz50qx">
                    We’ll take it from here.
                  </h4>
                  <p className="mt-5 max-w-md text-base leading-relaxed text-black/75" data-id="element-251" data-mp="1jdund549ydfj">
                    {statusMessage}
                  </p>
                  <div className="mt-auto flex flex-col gap-3 pt-10 sm:flex-row" data-id="element-252" data-mp="1yvm0kqj1yzvp">
                    <button type="button" onClick={resetForm} className="inline-flex min-h-12 items-center justify-center border border-black px-5 text-sm font-extrabold hover:bg-black hover:text-white" data-id="element-253" data-mp="1uvvciukwuxuv">
                      SEND ANOTHER BRIEF
                    </button>
                    <a href="mailto:info@samplerapp.com?subject=Campaign%20interest" className="inline-flex min-h-12 items-center justify-center gap-2 px-5 text-sm font-extrabold underline decoration-[#ff4d17] decoration-2 underline-offset-4 hover:text-black/65" data-id="element-254" data-mp="1fxt86i6228ck">
                      EMAIL SAMPLER <MailIcon size={16} aria-hidden="true" data-id="element-255" data-mp="125v6gseobeqf" />
                    </a>
                  </div>
                </motion.div> : <motion.form key="form" noValidate onSubmit={handleSubmit} initial={reduceMotion ? false : {
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: reduceMotion ? 0 : 0.32,
              ease: [0.22, 1, 0.36, 1]
            }} aria-describedby="campaign-form-status" data-id="element-256" data-mp="x7bjqps99yx0">
                  <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2" data-id="element-257" data-mp="s1ohdou2c8td">
                    <FormField label="Contact name" name="contactName" error={errors.contactName} data-id="element-258" data-mp="x111i1tjv1s9">
                      <input id="contactName" name="contactName" autoComplete="name" value={values.contactName} onChange={handleChange} className={getInputClassName(errors.contactName)} data-id="element-259" data-mp="3viol6dgxlxbb" />
                    </FormField>
                    <FormField label="Work email" name="workEmail" error={errors.workEmail} data-id="element-260" data-mp="9awynwo6r62b">
                      <input id="workEmail" name="workEmail" type="email" autoComplete="email" value={values.workEmail} onChange={handleChange} className={getInputClassName(errors.workEmail)} data-id="element-261" data-mp="e2osqh6jvtx7" />
                    </FormField>
                    <FormField label="Company" name="company" error={errors.company} data-id="element-262" data-mp="3hq8dv0gzzd4l">
                      <input id="company" name="company" autoComplete="organization" value={values.company} onChange={handleChange} className={getInputClassName(errors.company)} data-id="element-263" data-mp="1hbpj1bfmxogj" />
                    </FormField>
                    <FormField label="Role" name="role" error={errors.role} data-id="element-264" data-mp="1ra5dnodvffus">
                      <input id="role" name="role" autoComplete="organization-title" value={values.role} onChange={handleChange} className={getInputClassName(errors.role)} data-id="element-265" data-mp="1vb7ci43vlpw8" />
                    </FormField>
                    <FormField label="Campaign reason / use case" name="campaignReason" error={errors.campaignReason} data-id="element-266" data-mp="13iq6nib5gad7">
                      <select id="campaignReason" name="campaignReason" value={values.campaignReason} onChange={handleChange} className={getInputClassName(errors.campaignReason)} data-id="element-267" data-mp="17kbf8rgni2hl">
                        <option value="" data-id="element-268" data-mp="fuqfih9xehy3">Select a use case</option>
                        <option value="Product launch" data-id="element-269" data-mp="jvirvje3h2yl">Product launch</option>
                        <option value="Brand activation" data-id="element-270" data-mp="jtfqk37hrnek">
                          Brand activation
                        </option>
                        <option value="Sampling campaign" data-id="element-271" data-mp="7d6d24lp4w11">
                          Sampling campaign
                        </option>
                        <option value="Retail activation" data-id="element-272" data-mp="3a076y2hx6v3">
                          Retail activation
                        </option>
                        <option value="Event campaign" data-id="element-273" data-mp="3ujyx3g20a5ut">Event campaign</option>
                        <option value="Other" data-id="element-274" data-mp="3pk48xk9m7udg">Other</option>
                      </select>
                    </FormField>
                    <FormField label="Planned reach" name="plannedReach" error={errors.plannedReach} data-id="element-275" data-mp="3kpzxootbh06k">
                      <select id="plannedReach" name="plannedReach" value={values.plannedReach} onChange={handleChange} className={getInputClassName(errors.plannedReach)} data-id="element-276" data-mp="3fqdjod5nzfwp">
                        <option value="" data-id="element-277" data-mp="36rib4uf5dtq8">Select an estimate</option>
                        <option value="Under 1,000 people" data-id="element-278" data-mp="31sp2pgepwswx">
                          Under 1,000 people
                        </option>
                        <option value="1,000–5,000 people" data-id="element-279" data-mp="2s41kpv28u6zw">
                          1,000–5,000 people
                        </option>
                        <option value="5,001–25,000 people" data-id="element-280" data-mp="2i7f1fapkfnzz">
                          5,001–25,000 people
                        </option>
                        <option value="25,001–100,000 people" data-id="element-281" data-mp="2n71f1l9b369b">
                          25,001–100,000 people
                        </option>
                        <option value="100,000+ people" data-id="element-282" data-mp="3fuxjjc54a2gn">100,000+ people</option>
                        <option value="Still exploring" data-id="element-283" data-mp="3fx06toevxhho">Still exploring</option>
                      </select>
                    </FormField>
                    <FormField label="Campaign goals" name="goals" error={errors.goals} className="sm:col-span-2" data-id="element-284" data-mp="364m1ymsifura">
                      <textarea id="goals" name="goals" rows={3} value={values.goals} onChange={handleChange} className={getInputClassName(errors.goals)} placeholder="What should this product experience achieve?" data-id="element-285" data-mp="3ttngmrrxs250" />
                    </FormField>
                    <FormField label="Budget range" name="budgetRange" error={errors.budgetRange} data-id="element-286" data-mp="1n4d64ekc8bg4">
                      <select id="budgetRange" name="budgetRange" value={values.budgetRange} onChange={handleChange} className={getInputClassName(errors.budgetRange)} data-id="element-287" data-mp="1i5imxbigkkvh">
                        <option value="" data-id="element-288" data-mp="1rxodcgt2au8k">Select a range</option>
                        <option value="Under 25,000 SEK" data-id="element-289" data-mp="147262rqvtdzm">
                          Under 25,000 SEK
                        </option>
                        <option value="25,000–100,000 SEK" data-id="element-290" data-mp="za1ygcedw36e">
                          25,000–100,000 SEK
                        </option>
                        <option value="100,001–250,000 SEK" data-id="element-291" data-mp="1d5xemahef063">
                          100,001–250,000 SEK
                        </option>
                        <option value="250,001–500,000 SEK" data-id="element-292" data-mp="2omdv874ovmuj">
                          250,001–500,000 SEK
                        </option>
                        <option value="500,000+ SEK" data-id="element-293" data-mp="2knwmxk9j86bj">500,000+ SEK</option>
                        <option value="Still defining" data-id="element-294" data-mp="3fxbkran3tr2z">Still defining</option>
                      </select>
                    </FormField>
                    <FormField label="Target timing" name="timing" error={errors.timing} data-id="element-295" data-mp="3jzey64tc35qw">
                      <input id="timing" name="timing" value={values.timing} onChange={handleChange} className={getInputClassName(errors.timing)} placeholder="e.g. Autumn 2026" data-id="element-296" data-mp="3oyrj2k0na2qu" />
                    </FormField>
                    <FormField label="Target geography / location" name="geography" error={errors.geography} data-id="element-297" data-mp="2nnm3wstg8oy">
                      <input id="geography" name="geography" value={values.geography} onChange={handleChange} className={getInputClassName(errors.geography)} placeholder="City, region, country or multiple markets" data-id="element-298" data-mp="bi2261l67n94" />
                    </FormField>
                    <FormField label="Product / category" name="productCategory" error={errors.productCategory} data-id="element-299" data-mp="lh9huvfdntze">
                      <input id="productCategory" name="productCategory" value={values.productCategory} onChange={handleChange} className={getInputClassName(errors.productCategory)} placeholder="What should people experience?" data-id="element-300" data-mp="qfavv1xy5340" />
                    </FormField>
                    <FormField label="Additional notes" name="notes" optional className="sm:col-span-2" data-id="element-301" data-mp="20y4dmpb831fj">
                      <textarea id="notes" name="notes" rows={3} value={values.notes} onChange={handleChange} className={inputClassName} placeholder="Anything else that will help frame the conversation?" data-id="element-302" data-mp="1w0w4tfbys9qy" />
                    </FormField>
                  </div>

                  <div className={`mt-6 border p-4 ${errors.consent ? 'border-red-700 bg-red-50' : 'border-black/20 bg-white/60'}`} data-id="element-303" data-mp="2jxs0b2ldsxff">
                    <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold leading-relaxed" data-id="element-304" data-mp="2ezgoimbj0cuk">
                      <input id="consent" name="consent" type="checkbox" checked={values.consent} onChange={handleChange} className="mt-1 h-4 w-4 shrink-0 accent-[#11110f]" aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'consent-error' : undefined} data-id="element-305" data-mp="3ceumyhay09dd" />
                      <span data-id="element-306" data-mp="1ap6nc6vp9mu4">
                        I agree that Sampler may use these details to respond to
                        this campaign enquiry. <span aria-hidden="true" data-id="element-307" data-mp="vzqxqu7wnng0">*</span>
                        <span className="sr-only" data-id="element-308" data-mp="10ykfwzy502mi">Required</span>
                      </span>
                    </label>
                    {errors.consent && <p id="consent-error" className="mt-2 pl-7 text-sm font-bold text-red-800" data-id="element-309" data-mp="mxrjg9o9yxgr">
                        {errors.consent}
                      </p>}
                  </div>

                  <div className="mt-6 flex flex-col justify-between gap-5 border-t border-black/20 pt-6 sm:flex-row sm:items-center" data-id="element-310" data-mp="d2g903jwgcet">
                    <p id="campaign-form-status" className={`max-w-sm text-sm font-bold leading-relaxed ${status === 'error' ? 'text-red-800' : 'text-black/70'}`} role={status === 'error' ? 'alert' : 'status'} aria-live={status === 'error' ? 'assertive' : 'polite'} data-id="element-311" data-mp="i22o6ijag2q9">
                      {statusMessage || 'We’ll use your brief to prepare the right first conversation.'}
                    </p>
                    <button type="submit" disabled={status === 'submitting'} className="inline-flex min-h-14 shrink-0 items-center justify-center gap-2 bg-[#11110f] px-6 text-sm font-extrabold text-white hover:bg-black/80 disabled:cursor-wait disabled:opacity-70" data-id="element-312" data-mp="3rvdnv6kgic83">
                      {status === 'submitting' ? 'SENDING BRIEF…' : 'SEND CAMPAIGN BRIEF'}
                      {status !== 'submitting' && <ArrowRightIcon size={17} aria-hidden="true" data-id="element-313" data-mp="ei25flaizlsj" />}
                    </button>
                  </div>
                </motion.form>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}
type FormFieldProps = {
  children: React.ReactElement<React.AriaAttributes>;
  error?: string;
  label: string;
  name: keyof CampaignInterestValues;
  optional?: boolean;
  className?: string;
};
function FormField({
  children,
  error,
  label,
  name,
  optional = false,
  className = ''
}: FormFieldProps) {
  return <div className={className} data-id="element-314" data-mp="29d8utfxevgfx">
      <label htmlFor={name} className="text-sm font-extrabold" data-id="element-315" data-mp="29c7f2y5ga5rv">
        {label}{' '}
        {optional ? <span className="font-semibold text-black/55" data-id="element-316" data-mp="2e50kzpcnnk5j">(optional)</span> : <span aria-hidden="true" data-id="element-317" data-mp="2j43dkabg7xwm">*</span>}
      </label>
      {cloneElement(children, {
      'aria-invalid': Boolean(error),
      'aria-describedby': error ? `${name}-error` : undefined,
      'aria-required': !optional
    })}
      {error && <p id={`${name}-error`} className="mt-2 text-sm font-bold text-red-800" data-id="element-318" data-mp="1qffxqu9gy0qg">
          {error}
        </p>}
    </div>;
}
function getInputClassName(error?: string) {
  return `${inputClassName} ${error ? 'border-red-700 bg-red-50 focus:border-red-700 focus:ring-red-300' : 'border-black/25'}`;
}
function validateValues(values: CampaignInterestValues): FormErrors {
  return requiredFields.reduce<FormErrors>((nextErrors, fieldName) => {
    if (fieldName === 'consent') {
      if (!values.consent) {
        nextErrors.consent = 'Please acknowledge how Sampler may use this enquiry.';
      }
      return nextErrors;
    }
    const value = values[fieldName];
    if (typeof value === 'string' && value.trim().length === 0) {
      nextErrors[fieldName] = `${fieldLabels[fieldName]} is required.`;
    } else if (fieldName === 'workEmail' && !isValidEmail(value)) {
      nextErrors.workEmail = 'Enter a valid work email address.';
    }
    return nextErrors;
  }, {});
}
function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
function focusFirstError(errors: FormErrors) {
  const firstField = requiredFields.find(fieldName => errors[fieldName]);
  if (!firstField) return;
  window.requestAnimationFrame(() => {
    document.getElementById(firstField)?.focus();
  });
}